/** Dual-path upload: CSV → train model; PDF/DOCX/XLSX → agent RAG. */

/** Form + API train/docs upload max size. */
export const MAX_TRAIN_CSV_BYTES = 1 * 1024 * 1024;
export const MAX_UPLOAD_BYTES = MAX_TRAIN_CSV_BYTES;

export const FETCH_FAIL_USER_MESSAGE = "Something Wrong, Admin informed";

const TRAIN_CSV_EXTS = [".csv"] as const;
const AGENT_DOC_EXTS = [".pdf", ".docx", ".xlsx", ".xls"] as const;

export type UploadPath = "train" | "agent";

export function userFacingFetchError(err: unknown, fallback: string): string {
  const msg = err instanceof Error ? err.message : fallback;
  if (
    msg === "Failed to fetch" ||
    msg === "Load failed" ||
    msg.includes("NetworkError")
  ) {
    return FETCH_FAIL_USER_MESSAGE;
  }
  return msg || fallback;
}

export function trainCsvTooLargeMessage(size: number): string {
  const mb = (size / (1024 * 1024)).toFixed(2);
  return `File too large (${mb} MB). Maximum upload size is 1 MB.`;
}

export function trainUploadTypeErrorMessage(filename: string): string {
  return (
    `File type not allowed (${filename || "unknown"}). ` +
    "CSV trains a model; Word (.docx), Excel (.xlsx/.xls), or PDF (.pdf) use the document agent."
  );
}

function lowerName(filename: string): string {
  return (filename || "").trim().toLowerCase();
}

export function isTrainCsvFilename(filename: string): boolean {
  return TRAIN_CSV_EXTS.some((ext) => lowerName(filename).endsWith(ext));
}

export function isAgentDocFilename(filename: string): boolean {
  return AGENT_DOC_EXTS.some((ext) => lowerName(filename).endsWith(ext));
}

/** @deprecated use isTrainCsvFilename — kept for older callers */
export function isTrainUploadFilename(filename: string): boolean {
  return isTrainCsvFilename(filename) || isAgentDocFilename(filename);
}

export function uploadPathForFilename(filename: string): UploadPath | null {
  if (isTrainCsvFilename(filename)) return "train";
  if (isAgentDocFilename(filename)) return "agent";
  return null;
}

export function assertTrainCsvSize(file: File): void {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(trainCsvTooLargeMessage(file.size));
  }
}

/** Validate any dual-path upload (CSV or agent doc) and max 1 MB. */
export function assertTrainUploadFile(file: File): void {
  if (!uploadPathForFilename(file.name)) {
    throw new Error(trainUploadTypeErrorMessage(file.name));
  }
  assertTrainCsvSize(file);
}

/** Validate file for the chosen UI path (model vs agent). */
export function assertUploadForPath(file: File, path: UploadPath): void {
  assertTrainCsvSize(file);
  const actual = uploadPathForFilename(file.name);
  if (path === "train") {
    if (actual !== "train") {
      throw new Error(
        "Make AI models needs a CSV file (.csv). For Word, Excel, or PDF, choose “Ask AI about a file”.",
      );
    }
    return;
  }
  if (actual !== "agent") {
    throw new Error(
      "Ask AI about a file needs Word (.docx), Excel (.xlsx/.xls), or PDF (.pdf). For CSV, choose “Make AI models”.",
    );
  }
}

export function analyzeErrorMessage(
  res: { status: number },
  raw: string,
  data: { status?: string; message?: string },
): string {
  const fromJson = typeof data.message === "string" ? data.message.trim() : "";
  if (fromJson) return fromJson;
  if (res.status === 413 || /413|Request Entity Too Large/i.test(raw)) {
    return "File too large. Maximum upload size is 1 MB.";
  }
  if (raw && !raw.trim().startsWith("{")) {
    const snippet = raw.replace(/\s+/g, " ").trim().slice(0, 120);
    return `Analyze failed (HTTP ${res.status}): ${snippet || "non-JSON response"}`;
  }
  if (res.status >= 500) {
    return `Analyze failed (HTTP ${res.status}): train API server error.`;
  }
  if (res.status > 0) {
    return `Analyze failed (HTTP ${res.status}).`;
  }
  return "Analyze failed: empty response from train API.";
}
